import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Controls } from '../TripItems/Search/Common'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import TextLink from 'components/Luxkit/TextLink'
import SearchMenuListItem from 'components/Search/SearchForm/SearchMenu/SearchMenuListItem'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useGlobalSearchTypeahead from 'hooks/GlobalSearch/useGlobalSearchTypeahead'
import noop from 'lib/noop'
import { UpdateTripError } from 'tripPlanner/api/types'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface Props {
  tripId: string
}

const searchTypes = ['destination' as App.SearchType]
const typeaheadTypes = [
  'city',
  'multi_city_vicinity',
  'country',
] as App.SearchPlaceType[]

const UpdateTripOrigin = ({ tripId }: Props) => {
  const [locationName, setLocationName] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { suggestedSearchItems } = useContext(GlobalSearchStateContext)

  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal, goBack } = useModalCallbacks()

  const { mutate, isLoading: isSaving } = useUpdateTrip({
    onSuccess: () => {
      closeModal()
    },
    onError: (e: UpdateTripError) => {
      if (e.status === 422) {
        setErrorMsg(e.message)
      }
    },
  })
  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_LOCATION,
        location: e.currentTarget.value,
      })
      setLocationName(e.currentTarget.value)
      if (errorMsg) {
        setErrorMsg(undefined)
      }
    },
    [errorMsg, globalSearchDispatch],
  )

  const { isLoading } = useGlobalSearchTypeahead({
    search: locationName,
    searchTypes,
    typeaheadTypes,
  })

  const onClear = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_LOCATION })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
      searchItems: [],
    })
    setLocationName('')
  }, [globalSearchDispatch])

  const onSave = useCallback(
    (changedSearchItem: App.SearchItem) => {
      // update trip with new originPlaceId
      mutate({
        tripId,
        originPlaceId: changedSearchItem.value,
      })
    },
    [mutate, tripId],
  )

  useEffect(() => {
    if (inputRef) {
      inputRef.current?.focus()
    }
    return () => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_SUGGESTED_SEARCH_ITEMS,
        searchItems: [],
      })
    }
  }, [globalSearchDispatch])

  return (
    <>
      <ModalHeaderWrapper
        title="Edit departure location"
        onCloseButtonClick={isSaving ? noop : closeModal}
        onBackButtonClick={isSaving && goBack ? noop : goBack}
      >
        <Controls>
          <TextInput
            inputRef={inputRef}
            placeholder="Search by city or country"
            value={locationName}
            onChange={onInputChange}
            noValidationSpacing
          />
          {locationName.length > 1 && (
            <TextLink
              style={{ width: 'initial' }}
              variant="default"
              onClick={onClear}
            >
              Clear
            </TextLink>
          )}
        </Controls>
      </ModalHeaderWrapper>
      <ModalBody>
        <ModalContent>
          {!(isLoading || isSaving) &&
            suggestedSearchItems.map((item) => (
              <SearchMenuListItem
                key={item.value}
                searchItem={item}
                onClick={onSave}
              />
            ))}
          {(isLoading || isSaving) && <LoadingIndicator inline />}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default UpdateTripOrigin
